import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal, WritableSignal } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { HISTORY_OFFERS_QUERY, HISTORY_SERIES_QUERY } from '@shared/consts/graphql';
import { Offer, Series } from '@shared/graphql/generated';
import { CarItem } from '@shared/models/car-item';
import { ViewHistoryItem } from '@shared/models/view-history-item';
import { ViewHistoryItemType } from '@shared/models/view-history-item-type';
import { CarItemTransformService } from '@shared/services/car-item-transform.service';
import { ViewHistoryStoreService } from '@shared/stores/view-history-store.service';
import { CarListComponent } from '@shared/ui-components/car-list/car-list.component';
import { SectionLoaderComponent } from '@shared/ui-components/section-loader/section-loader.component';
import { Apollo } from 'apollo-angular';
import { forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';

@Component({
  selector: 'evland-view-history',
  templateUrl: './view-history.component.html',
  styleUrls: [ './view-history.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    CarListComponent,
    SectionLoaderComponent,
  ],
})
export class ViewHistoryComponent {
  cars$: Observable<CarItem[]>;
  isLoading: WritableSignal<boolean> = signal(true);

  #apollo = inject(Apollo);
  #carItemTransformService = inject(CarItemTransformService);
  #viewHistoryStore = inject(ViewHistoryStoreService);

  constructor() {
    this.initData();
  }

  private initData(): void {
    this.cars$ = this.#viewHistoryStore.list$
      .pipe(
        tap(() => this.isLoading.set(true)),
        switchMap((list: ViewHistoryItem[]) => {
          const offerIds: string[] = list.filter(({ type }) => type === ViewHistoryItemType.Offer).map(({ id }) => id);
          const seriesIds: string[] = list.filter(({ type }) => type === ViewHistoryItemType.Series).map(({ id }) => id);

          return forkJoin([
            of(list),
            !!offerIds.length ? this.#apollo.watchQuery({
              query: HISTORY_OFFERS_QUERY,
              variables: { ids: offerIds }
            }).valueChanges.pipe(take(1), map((result: ApolloQueryResult<{
              offers: Offer[]
            }>) => result.data.offers)) : of([]),
            !!seriesIds.length ? this.#apollo.watchQuery({
              query: HISTORY_SERIES_QUERY,
              variables: { ids: seriesIds }
            }).valueChanges.pipe(take(1), map((result: ApolloQueryResult<{
              series: Series[]
            }>) => result.data.series)) : of([]),
          ]);
        }),
        tap(([ list, offers, series ]: [ ViewHistoryItem[], Offer[], Series[] ]) => {
          if (list.length === [ ...offers, ...series ].length) {
            return;
          }

          this.#viewHistoryStore.list = list.filter(({
                                                      id,
                                                      type
                                                    }) => [ ...(type === ViewHistoryItemType.Offer ? offers : series) ].find((item: Offer | Series) => item.id));
        }),
        map(([ list, offers, series ]: [ ViewHistoryItem[], Offer[], Series[] ]) => list
          .map(({ id, type }) => ({
            data: [ ...(type === ViewHistoryItemType.Offer ? offers : series) ].find((item: Offer | Series) => id === item.id),
            id,
            type,
          }))
          .filter(({ data }) => !!data)
          .slice(0, 3)
          .map(({
                  data,
                  type
                }) => ({
            ...(type === ViewHistoryItemType.Offer ? this.#carItemTransformService.transformOffer(data as Offer) : this.#carItemTransformService.transformSeries(data as Series)),
            isHistoryItem: true,
          })),
        ),
        tap(() => this.isLoading.set(false)),
      );
  }
}
